import React, { useRef, useState, useEffect } from "react";
import styles from "./HorseInfoTab.module.css";
import i18n from "../../Services/i18n";
import ImagePreview from "../ImagePreview/ImagePreview";
import DateTimeService from "../../Services/datetimeService";
import TagsInput from "../TagsInput/TagsInput";
import FileUploaderModal from "../FileUploaderModal/FileUploaderModal";
import FileService from "../../Services/fileService";
import HorseService from "../../Services/horseService";
import { useHorses } from "../../Context/HorsesContext";
import LoadingSpinner from "../../LayoutComponents/LoadingSpinner";
import DateTimePicker from "../DateTimePicker/DateTimePicker";
import RichEditorHelper from "../../Tools/RichEditorHelper";
import RichEditor from "../RichEditor/RichEditor";
import { addToast } from "../../Services/toastService";
import { useDeletionConfirmationModal } from "../../Hooks/DeletionConfirmationModal";
import horseIcon from "../../Assets/Icons/horse_black.png";
import PrivilegeValidationService from "../../Services/privilegeValidationService";
import { usePrivileges } from "../../Context/PrivilegeContext";

const HorseInfoTab = ({ horse, reloadDetailHorse, refreshHorseList, lockParent }) => {
  const fileUploadModal = useRef(null);

  const descEditorRef = useRef(null);
  const [editorResetKey, setEditorResetKey] = useState(0);

  const [name, setName] = useState(horse.name);
  const [description, setDescription] = useState(horse.description);
  const [sex, setSex] = useState(horse.sex);
  const [breed, setBreed] = useState(horse.breed);
  const [coatColor, setCoatColor] = useState(horse.coatColor);
  const [birthDate, setBirthDate] = useState(horse.birthDate);
  const [defaultImage, setDefaultImage] = useState(horse.defaultImage);
  const [defaultImageId, setDefaultImageId] = useState(horse.defaultImageId);
  const [tags, setTags] = useState(horse.tags);
  const [isModified, setIsModified] = useState(false);
  const [canArchive, setCanArchive] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const confirmDelete = useDeletionConfirmationModal();
  const { updateHorse } = useHorses();
  const { privileges } = usePrivileges();

  useEffect(() => {
    const loadArchivePrivilege = async () => {
      setCanArchive(PrivilegeValidationService.CanArchive(await privileges()));
    };

    loadArchivePrivilege();
  }, []);

  useEffect(() => {
    setName(horse.name);
    setDescription(horse.description);
    setBreed(horse.breed);
    setBirthDate(DateTimeService.formatDateString(horse.birthDate, false));
    setCoatColor(horse.coatColor);
    setSex(horse.sex);
    setDefaultImageId(horse.defaultImageId);
    setDefaultImage(horse.defaultImage);
    setTags(horse.tags);
  }, [horse]);

  const initialData = {
    name: horse.name,
    description: horse.description,
    sex: horse.sex,
    birthDate: horse.birthDate,
    breed: horse.breed,
    coatColor: horse.coatColor,
    tags: horse.tags,
    defaultImageId: horse.defaultImageId,
    defaultImage: horse.defaultImage,
  };

  const isArchived = horse.isArchived === true;
  const handleArchiveClick = async () => {
    if (!isArchived && !canArchive) {
      addToast(i18n.t("Generic.PremiumFeatureOnly"), "ARCHIVE_PREMIUM_REQUIRED", "INFO");
      return;
    }

    try {
      setIsSaving(true);

      const result = isArchived
        ? await HorseService.UnarchiveHorse(horse.externalId)
        : await HorseService.ArchiveHorse(horse.externalId);

      if (result) {
        lockParent(false);
        setIsModified(false);

        await refreshHorseList(horse.externalId);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (setter) => (eOrValue) => {
    const value = eOrValue && eOrValue.target !== undefined ? eOrValue.target.value : eOrValue;
    setter(value);
    setIsModified(true);
    lockParent(true);
  };

  const handleDelete = () => {
    const horseTitle =
      horse.cardName ||
      [horse.firstName, horse.lastName].filter(Boolean).join(" ") ||
      horse.companyName ||
      "";

    confirmDelete({
      entityId: horse.externalId,
      entityTitle: horseTitle,
      entitySubTitle: null,
      icon: horseIcon,
      entityImage: horse.defaultImage,

      deleteFunction: (entityId) => HorseService.DeleteHorse(entityId),

      onClose: async (refreshNeeded) => {
        if (!refreshNeeded) return;

        lockParent(false);

        if (typeof refreshHorseList === "function") {
          await refreshHorseList();
        }
      },
    });
  };

  const handleChangeValue = (setter) => (value) => {
    setter(value);
    setIsModified(true);
    lockParent(true);
  };

  const handleReset = () => {
    setDefaultImage(initialData.defaultImage);
    setDefaultImageId(initialData.defaultImageId);
    setName(initialData.name);
    setBreed(initialData.breed);
    setBirthDate(initialData.birthDate);
    setCoatColor(initialData.coatColor);
    setSex(initialData.sex);
    setTags(initialData.tags);
    const initialHtml = horse.description || "";
    setDescription(initialHtml);
    descEditorRef.current?.setHTML(initialHtml);
    setEditorResetKey((k) => k + 1);

    setIsModified(false);
    lockParent(false);
  };

  const handleSave = async () => {
    const isValid = validate();
    if (!isValid) return;

    setIsSaving(true);
    const updatedHorseInfo = {
      externalId: horse.externalId,
      name: name,
      description: description,
      sex: sex ?? 0,
      birthDate: birthDate,
      breed: breed,
      coatColor: coatColor,
      tags: tags,
      defaultImageId: defaultImageId,
    };
    const result = await HorseService.Update(updatedHorseInfo);
    if (result) {
      reloadDetailHorse(horse);
      setIsModified(false);
      lockParent(false);
      updateHorse(horse.externalId);
    }

    setIsSaving(false);
  };

  const validate = () => {
    if (name === "") {
      addToast(i18n.t("HorseForm.Validation.NameRequired"), "horseFormValidation", "ERROR");
      return false;
    }

    if (description.length > 40000) {
      addToast(
        i18n.t("ClientForm.Validation.InvalidDescriptionLen"),
        "clientFormValidationEmail",
        "ERROR",
      );
      return false;
    }

    return true;
  };

  return (
    <>
      <div className={styles.container}>
        <div
          className={`${styles.content} ${isArchived ? styles.archivedReadOnly : ""}`}
          inert={isArchived}>
          <div className={styles.leftSection}>
            <div className={styles.formGroup}>
              <label className={styles.label}>{i18n.t("Generic.Name")}</label>
              <input
                type="text"
                className={styles.input}
                value={name}
                maxLength={200}
                onChange={handleChange(setName)}
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>{i18n.t("Generic.ClientName")}</label>
              <input type="text" className={styles.input} value={horse.clientName} disabled />
            </div>

            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label className={styles.label}>{i18n.t("Generic.Sex")}</label>
                <select value={sex} onChange={handleChange(setSex)} className={styles.select}>
                  <option key={0} value={0}>
                    {i18n.t("Generic.Sex.Undefined")}
                  </option>
                  <option key={1} value={1}>
                    {i18n.t("Generic.Sex.Guelding")}
                  </option>
                  <option key={2} value={2}>
                    {i18n.t("Generic.Sex.Female")}
                  </option>
                  <option key={3} value={3}>
                    {i18n.t("Generic.Sex.Male")}
                  </option>
                </select>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>{i18n.t("Generic.BirthDate")}</label>

                <DateTimePicker
                  name="birthdate"
                  value={birthDate}
                  onChange={handleChange(setBirthDate)}
                  readOnly={false}
                  includeTime={false}
                  showAgeBox={true}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>{i18n.t("Generic.Breed")}</label>
                <input
                  type="text"
                  className={styles.input}
                  value={breed}
                  maxLength={256}
                  onChange={handleChange(setBreed)}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>{i18n.t("Generic.CoatColor")}</label>
                <input
                  type="text"
                  className={styles.input}
                  value={coatColor}
                  maxLength={256}
                  onChange={handleChange(setCoatColor)}
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>{i18n.t("Generic.Description")}</label>

              <RichEditor
                ref={descEditorRef}
                key={`client-desc-${horse.externalId}-${editorResetKey}`}
                controlled
                value={description}
                onChange={(html) => {
                  setDescription(html);

                  const left = RichEditorHelper.normalizeHtmlForCompare(html);
                  const right = RichEditorHelper.normalizeHtmlForCompare(horse.description);

                  if (left !== right) {
                    setIsModified(true);
                    lockParent(true);
                  }
                }}
                placeholder={""}
                height={300}
                showEmoji
                showTextColor
                showFontSize
                showBold
                showItalic
                showUnderline
                showStrikethrough
              />
            </div>

            <div>
              <label className={styles.label}>{i18n.t("Generic.Tags")}</label>
              <TagsInput value={tags ?? ""} onChange={handleChangeValue(setTags)} />
            </div>
          </div>

          <div className={styles.rightSection}>
            <div className={styles.metaInfo}>
              <label>
                {i18n.t("ClientForm.CreationDate")}:{" "}
                {DateTimeService.formatDateString(horse.createdOn, true)}
              </label>
              {horse.modifiedOn && (
                <label>
                  {i18n.t("ClientForm.UpdateDate")}:{" "}
                  {DateTimeService.formatDateString(horse.modifiedOn, true)}
                </label>
              )}
            </div>

            <div className={styles.imgPreview}>
              <ImagePreview imageUrl={defaultImage} />
              <FileUploaderModal
                showMultiImageTab={false}
                ref={fileUploadModal}
                callback={async (thumbnail, fileId) => {
                  const img = await FileService.getFile(fileId);
                  setDefaultImage(img);
                  setDefaultImageId(fileId);
                  setIsModified(true);
                  lockParent(true);
                  fileUploadModal.current.close();
                }}
                allowMultipleFiles={false}
              />
              <div className={styles.buttonUpdateDefaultPictureContainer}>
                <button
                  className={styles.buttonUpdateDefaultPicture}
                  onClick={() => fileUploadModal.current.open()}>
                  {i18n.t("PictureUploadField.LoadAnImage")}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.footer}>
          <div className={styles.footerLeft}>
            <button
              className={styles.formDeleteButton}
              disabled={isSaving || isArchived}
              onClick={handleDelete}>
              {i18n.t("Generic.Delete")}
            </button>

            <div
              className={styles.archiveButtonWrapper}
              title={!canArchive ? i18n.t("Generic.PremiumFeatureOnly") : ""}>
              <button
                className={`${styles.formArchiveButton} ${
                  isArchived ? styles.formUnarchiveButton : ""
                } ${!isArchived && !canArchive ? styles.formArchiveButtonDisabled : ""}`}
                disabled={isSaving}
                aria-disabled={!isArchived && !canArchive}
                onClick={handleArchiveClick}>
                {isArchived ? i18n.t("Generic.Unarchive") : i18n.t("Generic.Archive")}
              </button>
            </div>
          </div>

          <div
            className={`${styles.footerRight} ${isArchived ? styles.archivedFooterActions : ""}`}>
            <button
              className={styles.formSaveButton}
              disabled={isArchived || !isModified}
              onClick={handleSave}>
              {i18n.t("Generic.Save")}
            </button>

            <button
              className={styles.formResetButton}
              disabled={isArchived || !isModified}
              onClick={handleReset}>
              {i18n.t("Generic.ResetForm")}
            </button>
          </div>
        </div>
        {isSaving && (
          <div className={styles.savingOverlay}>
            <LoadingSpinner />
          </div>
        )}
      </div>
    </>
  );
};

export default HorseInfoTab;
